import { Dialog, DialogPanel } from "@headlessui/react";
import { IoMdClose } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { ApiGet, ApiPost } from "../../helper/axiosFunctions";
import { commonToast } from "../../helper/Toast";
import { IoCheckmarkCircle, IoSearchOutline } from "react-icons/io5";
import { BsFolder2Open } from "react-icons/bs";
import Loading from "../Loading";
import Button from "../ui/Button";

export default function AssignToDeckModal({ 
    isOpen, 
    setIsOpen, 
    cardData,
    currentDeckId,
    complexity,
    onSuccess 
}) {
    const { t } = useTranslation();
    const user_data = useSelector((state) => state.profileData.profileData);
    
    const [decks, setDecks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDecks, setSelectedDecks] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [assigning, setAssigning] = useState(false);

    // Fetch user's decks
    const fetchDecks = useCallback(async () => {
        if (!user_data?.userId) return;
        
        setLoading(true);
        try {
            const response = await ApiGet(`/contents/decks/authors/${user_data.userId}`);
            const allDecks = response?.data || [];
            
            // Filter out deleted decks and current deck
            const filteredDecks = allDecks.filter(deck => {
                const status = deck?.status?.toUpperCase();
                const hiddenStatuses = ["DELETED", "INACTIVE", "REMOVED", "ARCHIVED", "DISABLED"];
                const isNotHidden = !hiddenStatuses.includes(status);
                const isNotCurrentDeck = deck.deckId !== currentDeckId;
                return isNotHidden && isNotCurrentDeck;
            });
            
            setDecks(filteredDecks);
        } catch (error) {
            commonToast(error?.message || 'Failed to fetch decks', 'error');
        } finally {
            setLoading(false);
        }
    }, [user_data?.userId, currentDeckId]);

    useEffect(() => {
        if (isOpen) {
            fetchDecks();
            setSelectedDecks([]);
            setSearchQuery('');
        }
    }, [isOpen, fetchDecks]);

    const handleClose = () => {
        setIsOpen(false);
        setSelectedDecks([]);
        setSearchQuery('');
    };

    const toggleDeckSelection = (deckId) => {
        setSelectedDecks(prev => 
            prev.includes(deckId) 
                ? prev.filter(id => id !== deckId)
                : [...prev, deckId]
        );
    };

    const handleAssign = async () => {
        if (selectedDecks.length === 0) {
            commonToast(t('selectAtLeastOneDeck') || 'Please select at least one deck', 'error');
            return;
        }

        setAssigning(true);
        let successCount = 0;
        let errorCount = 0;

        for (const deckId of selectedDecks) {
            try {
                await ApiPost('/contents/decks/cards', {
                    deckId,
                    card: {
                        cardId: cardData?.cardId,
                        complexity: complexity || 3,
                        sortOrder: 99
                    }
                });
                successCount++;
            } catch (error) {
                console.error(`Failed to assign to deck ${deckId}:`, error);
                errorCount++;
            }
        }

        setAssigning(false);

        if (successCount > 0) {
            commonToast(
                t('cardAssignedSuccess', { count: successCount }) || 
                `Card assigned to ${successCount} deck(s) successfully`,
                'success'
            );
            handleClose();
            if (onSuccess) onSuccess();
        }

        if (errorCount > 0) {
            commonToast(
                t('cardAssignedError', { count: errorCount }) || 
                `Failed to assign to ${errorCount} deck(s)`,
                'error'
            );
        }
    };

    // Filter decks based on search
    const filteredDecks = decks.filter(deck => 
        deck.deckName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        deck.mainSubject?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <Dialog
            open={isOpen}
            as="div"
            className="relative z-40 focus:outline-none"
            onClose={handleClose}
        >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                    <DialogPanel
                        transition
                        className="w-full relative max-w-lg rounded-2xl bg-white duration-300 ease-out data-[closed]:transform-[scale(95%)] data-[closed]:opacity-0 shadow-2xl"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-800">
                                    {t('assignToDeck') || 'Assign to Deck'}
                                </h2>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    {cardData?.cardName || 'Card'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="p-2 hover:bg-gray-100 rounded-full transition-all"
                            >
                                <IoMdClose size={22} className="text-gray-500" />
                            </button>
                        </div>

                        {/* Search */}
                        <div className="px-6 py-3 border-b border-gray-100">
                            <div className="relative">
                                <IoSearchOutline 
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" 
                                    size={20} 
                                />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder={t('searchDecks') || 'Search decks...'}
                                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm"
                                />
                            </div>
                        </div>

                        {/* Deck List */}
                        <div className="px-6 py-4 max-h-[350px] overflow-y-auto">
                            {loading ? (
                                <div className="flex items-center justify-center py-10">
                                    <Loading />
                                </div>
                            ) : filteredDecks.length === 0 ? (
                                <div className="text-center py-10">
                                    <BsFolder2Open className="mx-auto text-gray-300 mb-3" size={48} />
                                    <p className="text-gray-500">
                                        {searchQuery 
                                            ? (t('noDecksFound') || 'No decks found')
                                            : (t('noOtherDecks') || 'No other decks available')
                                        }
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {filteredDecks.map((deck) => {
                                        const isSelected = selectedDecks.includes(deck.deckId);
                                        return (
                                            <div
                                                key={deck.deckId}
                                                onClick={() => toggleDeckSelection(deck.deckId)}
                                                className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                                    isSelected 
                                                        ? 'border-blue-500 bg-blue-50' 
                                                        : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                                                }`}
                                            >
                                                {/* Deck Image or Icon */}
                                                <div className="w-12 h-12 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                                                    {deck.smallImageUrl ? (
                                                        <img 
                                                            src={deck.smallImageUrl} 
                                                            alt={deck.deckName}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <BsFolder2Open className="text-gray-400" size={20} />
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Deck Info */}
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-medium text-gray-800 truncate">
                                                        {deck.deckName || 'Unnamed Deck'}
                                                    </h3>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        {deck.mainSubject && (
                                                            <span className="text-xs text-gray-500">
                                                                {deck.mainSubject}
                                                            </span>
                                                        )}
                                                        {deck.cards && (
                                                            <span className="text-xs text-gray-400">
                                                                • {deck.cards.length} {t('cards') || 'cards'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Selection Indicator */}
                                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                                    isSelected 
                                                        ? 'border-blue-500 bg-blue-500' 
                                                        : 'border-gray-300'
                                                }`}>
                                                    {isSelected && (
                                                        <IoCheckmarkCircle className="text-white" size={16} />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500">
                                    {selectedDecks.length > 0 
                                        ? `${selectedDecks.length} ${t('selected') || 'selected'}`
                                        : t('selectDecks') || 'Select decks to assign'
                                    }
                                </span>
                                <div className="flex gap-3">
                                    <Button variant="secondary" onClick={handleClose}>
                                        {t('cancel') || 'Cancel'}
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleAssign}
                                        disabled={assigning || selectedDecks.length === 0}
                                    >
                                        {assigning ? (
                                            <>
                                                <Loading />
                                                {t('assigning') || 'Assigning...'}
                                            </>
                                        ) : (
                                            t('assign') || 'Assign'
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
